import { ImageProps } from '@/components/Image';
import { OptionalLink, OptionalLinkProps } from '@/components/OptionalLink';
import type { StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { mergeProps } from '@/utils/merge';
import type { ReactNode } from 'react';
import { ArticleTeaser } from './ArticleTeaser';

export interface StandaloneArticleTeaserDefaultProps extends StandaloneComponentProps<typeof ArticleTeaser> {
  caption?: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  header?: ReactNode;
  headline?: ReactNode;
  kicker?: ReactNode;
  links?: {
    article?: OptionalLinkProps;
    caption?: OptionalLinkProps;
  };
  image: ImageProps;
  subscriptionLogo?: ReactNode;
  subscriptionLogoOnImage?: boolean;
  isPaidArticle?: boolean;
}

export const StandaloneArticleTeaserDefault: StandaloneComponent<StandaloneArticleTeaserDefaultProps> = ({
  caption,
  description,
  footer,
  header,
  headline,
  image,
  kicker,
  links,
  subscriptionLogo,
  subscriptionLogoOnImage,
  isPaidArticle,
  ...props
}) => {
  const captionText = typeof caption === 'string' ? caption : 'Läs mer';
  const headlineText = typeof headline === 'string' ? headline : 'Läs mer';

  const showSubscriptionLogo = Boolean(isPaidArticle && subscriptionLogo);
  const subscriptionLogoElement = (
    <ArticleTeaser.SubscriptionLogo aria-label="betalt innehåll">{subscriptionLogo}</ArticleTeaser.SubscriptionLogo>
  );
  const media = <ArticleTeaser.Image {...mergeProps(props?.options?.$image, image)} />;

  return (
    <ArticleTeaser {...props}>
      {header && <ArticleTeaser.Header>{header}</ArticleTeaser.Header>}
      <OptionalLink
        {...mergeProps(
          {
            options: {
              'aria-label': `Läs artikeln: ${headlineText}`,
              title: headlineText,
            },
          },
          links?.article,
        )}
        content={
          showSubscriptionLogo && subscriptionLogoOnImage ? (
            <ArticleTeaser.Media>
              {media}
              {subscriptionLogoElement}
            </ArticleTeaser.Media>
          ) : (
            media
          )
        }
      />
      <ArticleTeaser.Group>
        {caption && (
          <ArticleTeaser.Caption>
            <OptionalLink
              {...mergeProps(
                {
                  options: {
                    'aria-label': `Kategori: ${captionText}`,
                    title: captionText,
                  },
                },
                links?.caption,
              )}
              content={caption}
            />
          </ArticleTeaser.Caption>
        )}
        {headline && (
          <OptionalLink
            {...mergeProps(
              {
                options: {
                  'aria-label': `Läs artikeln: ${headlineText}`,
                  title: headlineText,
                },
              },
              links?.article,
            )}
            content={
              <>
                {showSubscriptionLogo && !subscriptionLogoOnImage && subscriptionLogoElement}
                <ArticleTeaser.Headline>{headline}</ArticleTeaser.Headline>
              </>
            }
          />
        )}
        {description && <ArticleTeaser.Description>{description}</ArticleTeaser.Description>}
        {kicker && <ArticleTeaser.Description>{kicker}</ArticleTeaser.Description>}
      </ArticleTeaser.Group>
      {footer && <ArticleTeaser.Footer>{footer}</ArticleTeaser.Footer>}
    </ArticleTeaser>
  );
};
